import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { EyeIcon, EyeOffIcon } from "lucide-react"
import { useAuth } from "@/contexts/auth-context"
import { signupService } from "@/services/signup"

const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+={}\[\]:;"'<>,.?/\\|-]).{8,24}$/

export default function PasswordSetupPage() {
  const navigate = useNavigate()
  const {} = useAuth()
  
  // Form states
  const [name, setName] = useState<string>('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [validPassword, setValidPassword] = useState(false)
  const [validMatch, setValidMatch] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const submitInProgress = useRef(false)

  useEffect(() => {
    // Check for required tokens
    const checkTokens = setTimeout(() => {
      const resetToken = localStorage.getItem('reset_password_token')
      const authToken = localStorage.getItem('authToken')

      if (!resetToken || !authToken) {
        console.warn('Missing tokens - redirecting to signup', { 
          resetToken: !!resetToken, 
          authToken: !!authToken 
        })
        navigate('/signup')
        return
      }
    }, 100)

    return () => clearTimeout(checkTokens)
  }, [navigate])

  useEffect(() => {
    setValidPassword(PWD_REGEX.test(password))
  }, [password])

  useEffect(() => {
    setValidMatch(password === confirmPassword && confirmPassword !== '')
  }, [password, confirmPassword])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (submitInProgress.current) return

    if (!validPassword || !validMatch || !name.trim()) {
      setError('Please fill all fields correctly')
      return
    }

    submitInProgress.current = true
    setLoading(true)
    setError('')

    try {
      // Call the signup update API endpoint via the shared api client so it
      // resolves to the gateway base URL in production (a relative fetch to
      // /api/signup hits edge.glaium.io's nginx and 405s with an HTML page).
      const resetToken = localStorage.getItem('reset_password_token')

      const userData = await signupService.completeSignup({
        full_name: name.trim(),
        password,
        password_confirmation: confirmPassword,
        reset_password_token: resetToken || ''
      })

      // Store the returned JWT token
      if (userData.token) {
        localStorage.setItem('authToken', userData.token)
        
        // Store user data for auth context
        const user = {
          id: userData.id,
          email: userData.email,
          name: name.trim()
        }
        localStorage.setItem('user', JSON.stringify(user))
      }

      // Clean up the reset token
      localStorage.removeItem('reset_password_token')

      // Card-less free-tier onboarding: go straight to org creation (no Stripe
      // Checkout step). A card is only requested later, when the org crosses the
      // free-trial ceiling.
      navigate('/get-started/organization')
      
    } catch (err: any) {
      setError(err.message || 'Failed to set up password. Please try again.')
      console.error('Password setup error:', err)
    } finally {
      submitInProgress.current = false
      setLoading(false)
    }
  }

  return (
    <div className="container relative h-screen flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      <div className="relative hidden h-full flex-col bg-muted p-10 text-white lg:flex dark:border-r">
        <div className="absolute inset-0 bg-slate-700" />
        <div className="relative z-20 flex items-center text-lg font-medium">
          <img src="/logo_white.png" alt="Glaium Logo" className="h-8 w-auto" />
        </div>
      </div>
      <div className="flex h-full items-center p-4 lg:p-8 bg-muted/40">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Complete Your Setup
            </h1>
            <p className="text-sm text-muted-foreground">
              Set your name and password to finish creating your account
            </p>
          </div>
          
          <div className="grid gap-6">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a secure password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOffIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Password must be 8-24 characters with uppercase, lowercase, number, and special character
                  </div>
                  {password && !validPassword && (
                    <div className="text-xs text-red-600">
                      Password requirements not met
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <EyeOffIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {confirmPassword && !validMatch && (
                    <div className="text-xs text-red-600">
                      Passwords do not match
                    </div>
                  )}
                </div>

                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800 text-sm">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || !validPassword || !validMatch || !name.trim()}
                >
                  {loading ? 'Setting up...' : 'Complete Setup'}
                </Button>
              </form>
          </div>
        </div>
      </div>
    </div>
  )
}